import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, Stack, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import { IJournalEntry } from '../../interfaces/accounting.interface';
import { formatMoney, today } from './money';
import { useAccountingInstitution } from './useAccountingInstitution';

/** One journal entry with its lines. A posted entry can be reversed; entries are never edited. */
const JournalEntryDetail: React.FC = () => {
    const { id = '' } = useParams();
    const institutionId = useAccountingInstitution();
    const navigate = useNavigate();
    const { t } = useTranslation(['accounting', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [entry, setEntry] = useState<IJournalEntry | null>(null);
    const [reversing, setReversing] = useState(false);
    const [date, setDate] = useState(today());
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!institutionId || !id) return;
        AccountingService.getEntry(institutionId, id)
            .then(setEntry)
            .catch(error => showApiError(error, t('journalEntryDetail.loadFailed')));
    }, [institutionId, id]); // eslint-disable-line react-hooks/exhaustive-deps

    const reverse = async () => {
        setSaving(true);
        try {
            const reversal = await AccountingService.reverseEntry(institutionId, id, date, reason || undefined);
            showSuccess(reversal.entryNumber
                ? t('journalEntryDetail.reversedWith', { number: reversal.entryNumber })
                : t('journalEntryDetail.reversedWithNew'));
            setReversing(false);
            navigate(`/accounting/entries/${reversal.id}`);
        } catch (error) {
            showApiError(error, t('journalEntryDetail.reverseFailed'));
        } finally {
            setSaving(false);
        }
    };

    if (!entry) return <Box sx={{ p: 3 }}><Typography>{t('common:status.loading')}</Typography></Box>;

    const totalDebit = entry.lines.reduce((s, l) => s + (l.debit || 0), 0);
    const totalCredit = entry.lines.reduce((s, l) => s + (l.credit || 0), 0);

    return (
        <Box sx={{ p: 3, maxWidth: 960 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h5">{entry.entryNumber}</Typography>
                <Chip size="small" label={t(`sourceChips.${entry.source}`)} />
                {entry.status === 'REVERSED' && <Chip size="small" color="warning" label={t('chips.reversed')} />}
                <Box sx={{ flexGrow: 1 }} />
                <Button component={RouterLink} to="/accounting/entries">{t('journalEntryDetail.allEntries')}</Button>
                {entry.status === 'POSTED' && entry.source === 'YEAR_END_CLOSE' && (
                    <Button component={RouterLink} to="/accounting/closing">{t('journalEntryDetail.reopenYearToUndo')}</Button>
                )}
                {entry.status === 'POSTED' && entry.source !== 'YEAR_END_CLOSE' && entry.source !== 'REVERSAL' && (
                    <Button variant="outlined" color="warning" onClick={() => setReversing(true)}>{t('journalEntryDetail.reverse')}</Button>
                )}
            </Stack>
            <Typography color="text.secondary" sx={{ mb: 1 }}>{entry.entryDate} · {entry.description}</Typography>
            {entry.reversalOfId && (
                <Typography sx={{ mb: 1 }}>
                    <Trans t={t} i18nKey="journalEntryDetail.reverses"
                           components={{ link: <Link component={RouterLink} to={`/accounting/entries/${entry.reversalOfId}`} /> }} />
                </Typography>
            )}
            <Paper variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('fields.account')}</TableCell>
                            <TableCell>{t('fields.memo')}</TableCell>
                            <TableCell align="right">{t('fields.debit')}</TableCell>
                            <TableCell align="right">{t('fields.credit')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entry.lines.map((l, i) => (
                            <TableRow key={i}>
                                <TableCell>{l.accountCode} · {l.accountName}</TableCell>
                                <TableCell>{l.memo}</TableCell>
                                <TableCell align="right">{l.debit ? formatMoney(l.debit) : ''}</TableCell>
                                <TableCell align="right">{l.credit ? formatMoney(l.credit) : ''}</TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={2}><strong>{t('common:fields.total')}</strong></TableCell>
                            <TableCell align="right"><strong>{formatMoney(totalDebit)}</strong></TableCell>
                            <TableCell align="right"><strong>{formatMoney(totalCredit)}</strong></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={reversing} onClose={() => setReversing(false)} fullWidth maxWidth="xs">
                <DialogTitle>{t('journalEntryDetail.reverseTitle', { number: entry.entryNumber })}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {t('journalEntryDetail.reverseIntro')}
                    </Typography>
                    <Stack spacing={2}>
                        <TextField id="reverse-date" type="date" label={t('journalEntryDetail.reversalDate')} value={date}
                                   onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                        <TextField id="reverse-reason" label={t('common:fields.reason')} value={reason}
                                   onChange={e => setReason(e.target.value)} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReversing(false)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" color="warning" disabled={saving || !date} onClick={reverse}>
                        {t('journalEntryDetail.confirmReversal')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default JournalEntryDetail;
